import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, ExternalLink, LogOut, GripVertical } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { ICON_OPTIONS } from "../defaults";
import { ASSETS } from "../data";

const TABS = ["Homepage", "Product", "Features", "Protocol", "Compare", "1-on-1", "Testimonials", "FAQ", "Products", "Orders", "Members", "Newsletter", "Subscribers", "Settings", "Account"];

const Field = ({ label, value, onChange, textarea, placeholder, type = "text", testid }) => (
  <div>
    <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45 block mb-2">{label}</label>
    {textarea ? (
      <textarea data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder}
        className="w-full bg-black/40 border border-white/15 px-4 py-3 font-body text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-accent transition-colors resize-y" />
    ) : (
      <input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} placeholder={placeholder}
        className="w-full bg-black/40 border border-white/15 px-4 py-3 font-body text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-accent transition-colors" />
    )}
  </div>
);

const SectionCard = ({ children }) => (
  <div className="border border-white/10 bg-[#0a0a0a] p-6 lg:p-8 space-y-5">{children}</div>
);

const RowCard = ({ children, onRemove }) => (
  <div className="relative border border-white/10 bg-black/30 p-5 space-y-4">
    <button onClick={onRemove} className="absolute top-3 right-3 text-white/30 hover:text-accent transition-colors" title="Remove">
      <Trash2 size={16} />
    </button>
    {children}
  </div>
);

export default function Admin() {
  const { logout, user } = useAuth();
  const { content, refresh } = useContent();
  const [tab, setTab] = useState("Homepage");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(structuredClone(content)); }, [content]);

  const set = (path, value) => {
    setDraft((prev) => {
      const next = structuredClone(prev);
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", draft);
      await refresh();
      toast.success("Changes published to the live site.");
    } catch (e) {
      toast.error("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="GTG" className="h-8 w-8 object-cover rounded-sm" />
            <span className="font-display font-extrabold uppercase tracking-tight text-sm">Admin Console</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" target="_blank" className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-white flex items-center gap-1.5 transition-colors">
              View Site <ExternalLink size={13} />
            </Link>
            <button data-testid="logout-button" onClick={logout} className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-accent flex items-center gap-1.5 transition-colors">
              Logout <LogOut size={13} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* Nav */}
        <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`tab-${t.toLowerCase()}`}
              onClick={() => setTab(t)}
              className={`text-left font-mono text-xs uppercase tracking-[0.15em] px-4 py-3 whitespace-nowrap transition-colors ${tab === t ? "bg-accent text-white" : "text-white/50 hover:text-white hover:bg-white/5"}`}
            >
              {t}
            </button>
          ))}
        </nav>

        {/* Panel */}
        <main className="space-y-6 min-w-0">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h1 className="font-display font-black uppercase tracking-tight text-2xl">{tab}</h1>
            {["Homepage", "Product", "Features", "Protocol", "Compare", "1-on-1", "Testimonials", "FAQ", "Settings"].includes(tab) && (
              <button data-testid="save-content-button" onClick={save} disabled={saving}
                className="flex items-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Publish
              </button>
            )}
          </div>

          {tab === "Homepage" && (
            <div className="space-y-6">
              <SectionCard>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Hero</p>
                <Field label="Eyebrow / Badge" testid="hero-badge" value={draft.hero.badge} onChange={(v) => set(["hero", "badge"], v)} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Title line 1" value={draft.hero.titleTop} onChange={(v) => set(["hero", "titleTop"], v)} />
                  <Field label="Title line 2 (muted)" value={draft.hero.titleMuted} onChange={(v) => set(["hero", "titleMuted"], v)} />
                  <Field label="Title line 3 (lead)" value={draft.hero.titleLead} onChange={(v) => set(["hero", "titleLead"], v)} />
                  <Field label="Title line 3 (red accent)" value={draft.hero.titleAccent} onChange={(v) => set(["hero", "titleAccent"], v)} />
                </div>
                <Field label="Subtext" textarea value={draft.hero.subtext} onChange={(v) => set(["hero", "subtext"], v)} />
                <Field label="CTA note" value={draft.hero.ctaNote} onChange={(v) => set(["hero", "ctaNote"], v)} />
              </SectionCard>

              <SectionCard>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Promo Callout</p>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={!!draft.promo.enabled} onChange={(e) => set(["promo", "enabled"], e.target.checked)} className="accent-[#ff2a2a] h-4 w-4" />
                  <span className="font-mono text-xs uppercase tracking-[0.15em] text-white/70">Show promo callout on product</span>
                </label>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Promo code (display)" value={draft.promo.code} onChange={(v) => set(["promo", "code"], v)} placeholder="GHOST20" />
                  <Field label="Promo text" value={draft.promo.text} onChange={(v) => set(["promo", "text"], v)} placeholder="for 20% off" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">Note: this only changes the on-page text. The actual redeemable code lives in Stripe, ask the builder to add/change real codes.</p>
              </SectionCard>

              <SectionCard>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Sales Counter</p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Baseline number" type="number" testid="sales-baseline" value={draft.sales.baseline} onChange={(v) => set(["sales", "baseline"], v)} />
                  <div className="sm:col-span-2"><Field label="Label" value={draft.sales.label} onChange={(v) => set(["sales", "label"], v)} /></div>
                </div>
                <Field label="Sub-line" value={draft.sales.sub} onChange={(v) => set(["sales", "sub"], v)} />
              </SectionCard>
            </div>
          )}

          {tab === "Product" && (
            <SectionCard>
              <Field label="Product section title" value={draft.product.name} onChange={(v) => set(["product", "name"], v)} />
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent pt-2">What's Included (bullets)</p>
              {(draft.product.includes || []).map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <GripVertical size={16} className="text-white/20 flex-shrink-0" />
                  <input value={item} onChange={(e) => { const arr = [...draft.product.includes]; arr[i] = e.target.value; set(["product", "includes"], arr); }}
                    className="flex-1 bg-black/40 border border-white/15 px-4 py-2.5 font-body text-sm text-white focus:outline-none focus:border-accent" />
                  <button onClick={() => set(["product", "includes"], draft.product.includes.filter((_, j) => j !== i))} className="text-white/30 hover:text-accent"><Trash2 size={16} /></button>
                </div>
              ))}
              <button onClick={() => set(["product", "includes"], [...(draft.product.includes || []), "New feature"])} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-accent hover:text-white transition-colors"><Plus size={14} /> Add bullet</button>
            </SectionCard>
          )}

          {tab === "Features" && (
            <div className="space-y-6">
              <SectionCard>
                <Field label="Section heading" value={draft.whatsInside.heading} onChange={(v) => set(["whatsInside", "heading"], v)} />
                <Field label="Section sub-text" textarea value={draft.whatsInside.sub} onChange={(v) => set(["whatsInside", "sub"], v)} />
              </SectionCard>
              {(draft.whatsInside.features || []).map((f, i) => (
                <RowCard key={i} onRemove={() => set(["whatsInside", "features"], draft.whatsInside.features.filter((_, j) => j !== i))}>
                  <div className="grid sm:grid-cols-[160px_1fr] gap-4">
                    <div>
                      <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45 block mb-2">Icon</label>
                      <select value={f.icon} onChange={(e) => { const arr = structuredClone(draft.whatsInside.features); arr[i].icon = e.target.value; set(["whatsInside", "features"], arr); }}
                        className="w-full bg-black/40 border border-white/15 px-3 py-3 font-mono text-xs text-white focus:outline-none focus:border-accent">
                        {ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <Field label="Title" value={f.title} onChange={(v) => { const arr = structuredClone(draft.whatsInside.features); arr[i].title = v; set(["whatsInside", "features"], arr); }} />
                  </div>
                  <Field label="Description" textarea value={f.desc} onChange={(v) => { const arr = structuredClone(draft.whatsInside.features); arr[i].desc = v; set(["whatsInside", "features"], arr); }} />
                </RowCard>
              ))}
              <button onClick={() => set(["whatsInside", "features"], [...(draft.whatsInside.features || []), { icon: "Shield", title: "New feature", desc: "" }])} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-accent hover:text-white transition-colors"><Plus size={14} /> Add feature</button>
            </div>
          )}

          {tab === "Protocol" && (
            <div className="space-y-6">
              <SectionCard>
                <Field label="Section heading" value={draft.manifesto.heading} onChange={(v) => set(["manifesto", "heading"], v)} />
                <Field label="Section sub-text" textarea value={draft.manifesto.sub} onChange={(v) => set(["manifesto", "sub"], v)} />
                <Field label="Bonus line" value={draft.manifesto.bonus} onChange={(v) => set(["manifesto", "bonus"], v)} />
              </SectionCard>
              {(draft.manifesto.chapters || []).map((c, i) => (
                <RowCard key={i} onRemove={() => set(["manifesto", "chapters"], draft.manifesto.chapters.filter((_, j) => j !== i))}>
                  <div className="grid sm:grid-cols-[120px_1fr] gap-4">
                    <Field label="Node #" value={c.node} onChange={(v) => { const arr = structuredClone(draft.manifesto.chapters); arr[i].node = v; set(["manifesto", "chapters"], arr); }} />
                    <Field label="Title" value={c.title} onChange={(v) => { const arr = structuredClone(draft.manifesto.chapters); arr[i].title = v; set(["manifesto", "chapters"], arr); }} />
                  </div>
                  <Field label="Description" textarea value={c.desc} onChange={(v) => { const arr = structuredClone(draft.manifesto.chapters); arr[i].desc = v; set(["manifesto", "chapters"], arr); }} />
                </RowCard>
              ))}
              <button onClick={() => set(["manifesto", "chapters"], [...(draft.manifesto.chapters || []), { node: String((draft.manifesto.chapters.length + 1)).padStart(2, "0"), title: "New node", desc: "" }])} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-accent hover:text-white transition-colors"><Plus size={14} /> Add node</button>
            </div>
          )}

          {tab === "Testimonials" && (
            <div className="space-y-6">
              {(draft.testimonials || []).map((t, i) => (
                <RowCard key={i} onRemove={() => set(["testimonials"], draft.testimonials.filter((_, j) => j !== i))}>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Handle / Name" value={t.handle} onChange={(v) => { const arr = structuredClone(draft.testimonials); arr[i].handle = v; set(["testimonials"], arr); }} />
                    <Field label="Role" value={t.role} onChange={(v) => { const arr = structuredClone(draft.testimonials); arr[i].role = v; set(["testimonials"], arr); }} />
                  </div>
                  <Field label="Quote" textarea value={t.quote} onChange={(v) => { const arr = structuredClone(draft.testimonials); arr[i].quote = v; set(["testimonials"], arr); }} />
                </RowCard>
              ))}
              <button onClick={() => set(["testimonials"], [...(draft.testimonials || []), { handle: "NEW_USER", role: "Customer", quote: "" }])} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-accent hover:text-white transition-colors"><Plus size={14} /> Add testimonial</button>
            </div>
          )}

          {tab === "FAQ" && (
            <div className="space-y-6">
              {(draft.faqs || []).map((f, i) => (
                <RowCard key={i} onRemove={() => set(["faqs"], draft.faqs.filter((_, j) => j !== i))}>
                  <Field label="Question" value={f.q} onChange={(v) => { const arr = structuredClone(draft.faqs); arr[i].q = v; set(["faqs"], arr); }} />
                  <Field label="Answer" textarea value={f.a} onChange={(v) => { const arr = structuredClone(draft.faqs); arr[i].a = v; set(["faqs"], arr); }} />
                </RowCard>
              ))}
              <button onClick={() => set(["faqs"], [...(draft.faqs || []), { q: "New question?", a: "" }])} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-accent hover:text-white transition-colors"><Plus size={14} /> Add FAQ</button>
            </div>
          )}

          {tab === "Compare" && draft.comparison && (
            <div className="space-y-6">
              <SectionCard>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Comparison Section</p>
                <Field label="Heading" value={draft.comparison.heading} onChange={(v) => set(["comparison", "heading"], v)} />
                <Field label="Sub-text" textarea value={draft.comparison.sub} onChange={(v) => set(["comparison", "sub"], v)} />
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Your name" value={draft.comparison.ourName} onChange={(v) => set(["comparison", "ourName"], v)} />
                  <Field label="Your price" value={draft.comparison.ourPrice} onChange={(v) => set(["comparison", "ourPrice"], v)} />
                  <Field label="Your cadence" value={draft.comparison.ourCadence} onChange={(v) => set(["comparison", "ourCadence"], v)} />
                </div>
              </SectionCard>

              <SectionCard>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Competitors</p>
                {(draft.comparison.competitors || []).map((comp, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
                    <Field label={i === 0 ? "Name" : ""} value={comp.name} onChange={(v) => { const arr = structuredClone(draft.comparison.competitors); arr[i].name = v; set(["comparison", "competitors"], arr); }} />
                    <Field label={i === 0 ? "Price" : ""} value={comp.price} onChange={(v) => { const arr = structuredClone(draft.comparison.competitors); arr[i].price = v; set(["comparison", "competitors"], arr); }} />
                    <Field label={i === 0 ? "Cadence" : ""} value={comp.cadence} onChange={(v) => { const arr = structuredClone(draft.comparison.competitors); arr[i].cadence = v; set(["comparison", "competitors"], arr); }} />
                    <button onClick={() => set(["comparison", "competitors"], draft.comparison.competitors.filter((_, j) => j !== i))} className="text-white/30 hover:text-accent pb-3"><Trash2 size={16} /></button>
                  </div>
                ))}
                <button onClick={() => set(["comparison", "competitors"], [...(draft.comparison.competitors || []), { name: "New", price: "$10", cadence: "/mo" }])} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-accent hover:text-white transition-colors"><Plus size={14} /> Add competitor</button>
              </SectionCard>

              <SectionCard>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Comparison rows (your advantages)</p>
                {(draft.comparison.rows || []).map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={row} onChange={(e) => { const arr = [...draft.comparison.rows]; arr[i] = e.target.value; set(["comparison", "rows"], arr); }}
                      className="flex-1 bg-black/40 border border-white/15 px-4 py-2.5 font-body text-sm text-white focus:outline-none focus:border-accent" />
                    <button onClick={() => set(["comparison", "rows"], draft.comparison.rows.filter((_, j) => j !== i))} className="text-white/30 hover:text-accent"><Trash2 size={16} /></button>
                  </div>
                ))}
                <button onClick={() => set(["comparison", "rows"], [...(draft.comparison.rows || []), "New advantage"])} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-accent hover:text-white transition-colors"><Plus size={14} /> Add row</button>
              </SectionCard>

              {draft.addon && (
                <SectionCard>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Opt-Out Directory Add-On</p>
                  <Field label="Heading" value={draft.addon.heading} onChange={(v) => set(["addon", "heading"], v)} />
                  <Field label="Product name" value={draft.addon.name} onChange={(v) => set(["addon", "name"], v)} />
                  <Field label="Description" textarea value={draft.addon.desc} onChange={(v) => set(["addon", "desc"], v)} />
                  {(draft.addon.bullets || []).map((b, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input value={b} onChange={(e) => { const arr = [...draft.addon.bullets]; arr[i] = e.target.value; set(["addon", "bullets"], arr); }}
                        className="flex-1 bg-black/40 border border-white/15 px-4 py-2.5 font-body text-sm text-white focus:outline-none focus:border-accent" />
                      <button onClick={() => set(["addon", "bullets"], draft.addon.bullets.filter((_, j) => j !== i))} className="text-white/30 hover:text-accent"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  <button onClick={() => set(["addon", "bullets"], [...(draft.addon.bullets || []), "New bullet"])} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-accent hover:text-white transition-colors"><Plus size={14} /> Add bullet</button>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">The $9 directory price is managed in the Products tab / Stripe. This only edits the marketing copy.</p>
                </SectionCard>
              )}
            </div>
          )}

          {tab === "1-on-1" && draft.consulting && (
            <div className="space-y-6">
              <SectionCard>
                <Field label="Section heading" value={draft.consulting.heading} onChange={(v) => set(["consulting", "heading"], v)} />
                <Field label="Sub-text" textarea value={draft.consulting.sub} onChange={(v) => set(["consulting", "sub"], v)} />
                <Field label="Note under tiers" textarea value={draft.consulting.note} onChange={(v) => set(["consulting", "note"], v)} />
              </SectionCard>
              <SectionCard>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Your Certifications (trust badges)</p>
                {(draft.consulting.credentials || []).map((cr, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={cr} onChange={(e) => { const arr = [...draft.consulting.credentials]; arr[i] = e.target.value; set(["consulting", "credentials"], arr); }}
                      className="flex-1 bg-black/40 border border-white/15 px-4 py-2.5 font-body text-sm text-white focus:outline-none focus:border-accent" />
                    <button onClick={() => set(["consulting", "credentials"], draft.consulting.credentials.filter((_, j) => j !== i))} className="text-white/30 hover:text-accent"><Trash2 size={16} /></button>
                  </div>
                ))}
                <button onClick={() => set(["consulting", "credentials"], [...(draft.consulting.credentials || []), "New certification"])} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-accent hover:text-white transition-colors"><Plus size={14} /> Add certification</button>
              </SectionCard>
              {(draft.consulting.tiers || []).map((t, i) => (
                <RowCard key={i} onRemove={() => set(["consulting", "tiers"], draft.consulting.tiers.filter((_, j) => j !== i))}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">Price for this tier is set in the Products tab (Stripe) · lookup: {t.lookup_key}</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Tier name" value={t.name} onChange={(v) => { const arr = structuredClone(draft.consulting.tiers); arr[i].name = v; set(["consulting", "tiers"], arr); }} />
                    <Field label="Duration / tagline" value={t.duration} onChange={(v) => { const arr = structuredClone(draft.consulting.tiers); arr[i].duration = v; set(["consulting", "tiers"], arr); }} />
                  </div>
                  <Field label="Description" textarea value={t.desc} onChange={(v) => { const arr = structuredClone(draft.consulting.tiers); arr[i].desc = v; set(["consulting", "tiers"], arr); }} />
                  <Field label="Features (comma-separated)" value={(t.features || []).join(", ")} onChange={(v) => { const arr = structuredClone(draft.consulting.tiers); arr[i].features = v.split(",").map((x) => x.trim()).filter(Boolean); set(["consulting", "tiers"], arr); }} />
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={!!t.featured} onChange={(e) => { const arr = structuredClone(draft.consulting.tiers); arr.forEach((x) => (x.featured = false)); arr[i].featured = e.target.checked; set(["consulting", "tiers"], arr); }} className="accent-[#ff2a2a] h-4 w-4" />
                    <span className="font-mono text-xs uppercase tracking-[0.15em] text-white/70">Mark as "Most Popular"</span>
                  </label>
                </RowCard>
              ))}
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">To add a brand-new tier, first create the product in the Products tab, then add a row here using its lookup key.</p>
            </div>
          )}

          {tab === "Settings" && draft.settings && (
            <SectionCard>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Live Chat & Badges</p>
              <Field label="Tawk.to ID or embed URL" testid="tawkto-id" value={draft.settings.tawkto} onChange={(v) => set(["settings", "tawkto"], v)} placeholder="propertyId/widgetId or https://embed.tawk.to/…" />
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">Paste the full embed.tawk.to link OR just the "propertyId/widgetId". Leave blank to hide the chat widget.</p>
              <Field label="Guarantee badge text (blank to hide)" value={draft.settings.guarantee} onChange={(v) => set(["settings", "guarantee"], v)} />
            </SectionCard>
          )}

          {tab === "Products" && <ProductsTab />}
          {tab === "Orders" && <OrdersTab />}
          {tab === "Members" && <MembersTab />}
          {tab === "Newsletter" && <NewsletterTab />}
          {tab === "Subscribers" && <SubscribersTab />}
          {tab === "Account" && <AccountTab email={user?.email} />}
        </main>
      </div>
    </div>
  );
}

/* ---------- Products ---------- */
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", description: "", amount: "", featured: false });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/products"); setProducts(data); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.amount) return toast.error("Name and price are required.");
    setCreating(true);
    try {
      await api.post("/admin/products", { name: form.name, description: form.description, amount: Number(form.amount), featured: form.featured });
      toast.success("Product created in Stripe.");
      setForm({ name: "", description: "", amount: "", featured: false });
      load();
    } catch { toast.error("Failed to create product."); } finally { setCreating(false); }
  };

  const feature = async (id) => { try { await api.put(`/admin/products/${id}`, { featured: true }); toast.success("Featured product updated."); load(); } catch { toast.error("Failed."); } };
  const toggleActive = async (p) => { try { await api.put(`/admin/products/${p.id}`, { active: !p.active }); load(); } catch { toast.error("Failed."); } };
  const remove = async (id) => { try { await api.delete(`/admin/products/${id}`); toast.success("Product archived."); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); } };
  const updatePrice = async (id, dollars) => { try { await api.put(`/admin/products/${id}`, { amount: Number(dollars) }); toast.success("Price updated in Stripe."); load(); } catch { toast.error("Failed."); } };

  return (
    <div className="space-y-6">
      <SectionCard>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Add Product</p>
        <form onSubmit={create} className="space-y-4">
          <Field label="Name" testid="new-product-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Description" textarea value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Price (USD)" type="number" testid="new-product-price" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} placeholder="29" />
            <label className="flex items-end gap-3 cursor-pointer pb-3">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#ff2a2a] h-4 w-4" />
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-white/70">Make this the featured (main) product</span>
            </label>
          </div>
          <button data-testid="create-product-button" type="submit" disabled={creating} className="flex items-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-60">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create in Stripe
          </button>
        </form>
      </SectionCard>

      {loading ? <Loader2 className="animate-spin text-white/40" /> : (
        <div className="space-y-3">
          {products.map((p) => (
            <div key={p.id} data-testid={`product-row-${p.id}`} className="border border-white/10 bg-[#0a0a0a] p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-3">
                  <span className="font-display font-bold text-lg truncate">{p.name}</span>
                  {p.featured && <span className="font-mono text-[10px] uppercase tracking-[0.15em] bg-accent text-white px-2 py-0.5">Featured</span>}
                  {!p.active && <span className="font-mono text-[10px] uppercase tracking-[0.15em] border border-white/20 text-white/50 px-2 py-0.5">Archived</span>}
                </div>
                <p className="font-mono text-xs text-white/40 mt-1">${((p.amount || 0) / 100).toFixed(2)} · {p.lookup_key}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => { const v = prompt("New price (USD):", ((p.amount || 0) / 100).toFixed(2)); if (v) updatePrice(p.id, v); }} className="font-mono text-[11px] uppercase tracking-[0.15em] border border-white/15 px-3 py-2 hover:border-accent hover:text-accent transition-colors">Edit price</button>
                {!p.featured && p.active && <button onClick={() => feature(p.id)} className="font-mono text-[11px] uppercase tracking-[0.15em] border border-white/15 px-3 py-2 hover:border-accent hover:text-accent transition-colors">Feature</button>}
                <button onClick={() => toggleActive(p)} className="font-mono text-[11px] uppercase tracking-[0.15em] border border-white/15 px-3 py-2 hover:border-accent hover:text-accent transition-colors">{p.active ? "Hide" : "Show"}</button>
                {!p.featured && <button onClick={() => remove(p.id)} className="text-white/30 hover:text-accent p-2"><Trash2 size={16} /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Orders ---------- */
function OrdersTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/orders").then((r) => setData(r.data)).catch(() => setData({ count: 0, orders: [] })); }, []);
  if (!data) return <Loader2 className="animate-spin text-white/40" />;
  return (
    <SectionCard>
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">{data.count} paid orders</p>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead><tr className="border-b border-white/10">{["Date", "Email", "Amount", "Session"].map((h) => <th key={h} className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 py-3 pr-4">{h}</th>)}</tr></thead>
          <tbody>
            {data.orders.map((o, i) => (
              <tr key={i} className="border-b border-white/5">
                <td className="font-mono text-xs text-white/60 py-3 pr-4 whitespace-nowrap">{(o.created_at || "").slice(0, 10)}</td>
                <td className="font-body text-sm py-3 pr-4">{o.email || "-"}</td>
                <td className="font-mono text-xs py-3 pr-4">${((o.amount || 0) / 100).toFixed(2)}</td>
                <td className="font-mono text-[10px] text-white/30 py-3 pr-4 truncate max-w-[160px]">{o.session_id}</td>
              </tr>
            ))}
            {data.orders.length === 0 && <tr><td colSpan={4} className="font-mono text-xs text-white/40 py-6">No paid orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

/* ---------- Members (portal access) ---------- */
function MembersTab() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ email: "", access_level: "full_pack", send_email: true });
  const [granting, setGranting] = useState(false);
  const [lastLink, setLastLink] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/members"); setData(data); } catch { setData({ count: 0, members: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const grant = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error("Enter an email.");
    setGranting(true);
    setLastLink(null);
    try {
      const { data } = await api.post("/admin/grant-access", form);
      toast.success(`${data.is_new ? "Account created" : "Access updated"}${data.emailed ? " and email sent" : ""}.`);
      if (!data.has_password) setLastLink(data.set_password_link);
      setForm({ email: "", access_level: form.access_level, send_email: true });
      load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed to grant access."); } finally { setGranting(false); }
  };

  const copyLink = () => { navigator.clipboard.writeText(lastLink); toast.success("Set-password link copied."); };

  const ACCESS_LABEL = { full_pack: "Full pack", directory: "Directory only", subscription: "Subscription" };

  return (
    <div className="space-y-6">
      <SectionCard>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Grant portal access</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">Comp an account or migrate a past customer. They'll get an email with a set-password link. Use your own email here to log into the member portal yourself.</p>
        <form onSubmit={grant} className="space-y-4">
          <Field label="Customer email" testid="grant-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="customer@email.com" />
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45 block mb-2">Access level</label>
              <select data-testid="grant-access-level" value={form.access_level} onChange={(e) => setForm({ ...form, access_level: e.target.value })}
                className="w-full bg-black/40 border border-white/15 px-3 py-3 font-mono text-xs text-white focus:outline-none focus:border-accent">
                <option value="full_pack">Full pack (all 12 + bonus)</option>
                <option value="directory">Directory only (Node 4)</option>
              </select>
            </div>
            <label className="flex items-end gap-3 cursor-pointer pb-3">
              <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} className="accent-[#ff2a2a] h-4 w-4" />
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-white/70">Email the set-password link</span>
            </label>
          </div>
          <button data-testid="grant-access-button" type="submit" disabled={granting} className="flex items-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-60">
            {granting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Grant access
          </button>
        </form>
        {lastLink && (
          <div className="border border-accent/40 bg-[#160606] p-4 space-y-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-accent">Set-password link (share if email didn't arrive)</p>
            <div className="flex items-stretch gap-2">
              <input readOnly value={lastLink} data-testid="grant-link-output" className="flex-1 bg-black/40 border border-white/15 px-3 py-2 font-mono text-[11px] text-white/70 focus:outline-none truncate" />
              <button onClick={copyLink} className="font-mono text-[11px] uppercase tracking-[0.15em] border border-white/15 px-4 hover:border-accent hover:text-accent transition-colors">Copy</button>
            </div>
          </div>
        )}
      </SectionCard>

      {!data ? <Loader2 className="animate-spin text-white/40" /> : (
        <SectionCard>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">{data.count} members</p>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead><tr className="border-b border-white/10">{["Email", "Access", "Referrals", "Password", "Joined"].map((h) => <th key={h} className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 py-3 pr-4">{h}</th>)}</tr></thead>
              <tbody>
                {data.members.map((m) => (
                  <tr key={m.id} data-testid={`member-row-${m.email}`} className="border-b border-white/5">
                    <td className="font-body text-sm py-3 pr-4">{m.email}</td>
                    <td className="font-mono text-[11px] py-3 pr-4 text-white/70">{(m.entitlements || []).map((e) => ACCESS_LABEL[e] || e).join(", ") || "-"}</td>
                    <td className="font-mono text-xs py-3 pr-4 text-white/70">{m.referrals || 0}</td>
                    <td className="py-3 pr-4">{m.has_password ? <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-400">Set</span> : <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/30">Pending</span>}</td>
                    <td className="font-mono text-[10px] text-white/30 py-3 pr-4 whitespace-nowrap">{(m.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
                {data.members.length === 0 && <tr><td colSpan={5} className="font-mono text-xs text-white/40 py-6">No members yet. Grant access above to create one.</td></tr>}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

/* ---------- Newsletter (biweekly AI) ---------- */
function NewsletterTab() {
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [voice, setVoice] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/newsletter"); setData(data); setVoice(data.voice || ""); } catch { setData(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    try { const { data: r } = await api.put("/admin/newsletter", { enabled: !data.enabled }); toast.success(r.enabled ? "Automated newsletter ON" : "Automated newsletter OFF"); load(); }
    catch { toast.error("Failed to update."); }
  };
  const saveVoice = async () => {
    setBusy("voice");
    try { await api.put("/admin/newsletter", { voice }); toast.success("Voice saved. Future issues will follow it."); load(); }
    catch { toast.error("Couldn't save voice."); } finally { setBusy(""); }
  };
  const doPreview = async () => {
    setBusy("preview"); setPreview(null);
    try { const { data: r } = await api.post("/admin/newsletter/preview", { voice }); setPreview(r); }
    catch { toast.error("Couldn't generate a preview."); } finally { setBusy(""); }
  };
  const sendNow = async () => {
    if (!window.confirm("Generate a fresh issue and email it to everyone now?")) return;
    setBusy("send");
    try { const { data: r } = await api.post("/admin/newsletter/send-now"); toast.success(`Sent to ${r.sent} of ${r.recipients}.`); load(); }
    catch { toast.error("Send failed."); } finally { setBusy(""); }
  };

  if (!data) return <Loader2 className="animate-spin text-white/40" />;

  return (
    <div className="space-y-6">
      <SectionCard>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Automated newsletter</p>
            <p className="font-body text-sm text-white/60 mt-1">AI writes a privacy tip and emails it automatically. {data.schedule}.</p>
          </div>
          <button data-testid="newsletter-toggle" onClick={toggle}
            className={`font-mono text-xs uppercase tracking-[0.15em] px-5 py-3 transition-colors ${data.enabled ? "bg-accent text-white hover:bg-white hover:text-black" : "border border-white/20 text-white/70 hover:border-accent hover:text-accent"}`}>
            {data.enabled ? "On · Click to pause" : "Off · Click to enable"}
          </button>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 pt-2">
          <div><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">Recipients</span><span data-testid="newsletter-recipients" className="font-display font-black text-2xl text-white">{data.recipient_count}</span></div>
          <div><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">Cadence</span><span className="font-display font-black text-2xl text-white">{data.cadence_days}d</span></div>
          <div><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">Last sent</span><span className="font-body text-sm text-white/70">{data.last_sent_at ? new Date(data.last_sent_at).toLocaleDateString() : "Never"}</span></div>
        </div>
        <div className="flex flex-wrap gap-3 pt-2">
          <button data-testid="newsletter-preview-button" onClick={doPreview} disabled={busy === "preview"}
            className="flex items-center gap-2 border border-white/15 text-white/80 font-mono uppercase tracking-[0.15em] text-xs px-5 py-3 hover:border-accent hover:text-white transition-colors disabled:opacity-60">
            {busy === "preview" ? <Loader2 size={14} className="animate-spin" /> : "Preview a sample"}
          </button>
          <button data-testid="newsletter-send-button" onClick={sendNow} disabled={busy === "send"}
            className="flex items-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-60">
            {busy === "send" ? <Loader2 size={14} className="animate-spin" /> : "Send an issue now"}
          </button>
        </div>
      </SectionCard>

      <SectionCard>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Voice & focus</p>
        <p className="font-body text-sm text-white/55">Steer what the AI writes. Examples: "Focus on families and older parents", "More technical, assume a tech-savvy reader", "Keep it under 120 words and blunt".</p>
        <textarea data-testid="newsletter-voice-input" value={voice} onChange={(e) => setVoice(e.target.value)} rows={3}
          placeholder="e.g. Speak to non-technical people protecting their family. Warm but no-nonsense."
          className="w-full bg-black/40 border border-white/15 px-4 py-3 font-body text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-accent transition-colors resize-y" />
        <button data-testid="newsletter-voice-save" onClick={saveVoice} disabled={busy === "voice"}
          className="flex items-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-60">
          {busy === "voice" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save voice
        </button>
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/35">"Preview a sample" above uses whatever is in this box right now, so you can experiment before saving.</p>
      </SectionCard>

      {preview && (
        <SectionCard>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">AI preview (not sent)</p>
          <p data-testid="newsletter-preview-subject" className="font-display font-bold text-lg">{preview.subject}</p>
          <div className="font-body text-sm text-white/65 leading-relaxed whitespace-pre-wrap border-l-2 border-accent/40 pl-4">{preview.body}</div>
        </SectionCard>
      )}

      <SectionCard>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Sent issues</p>
        {(data.history || []).length === 0 ? (
          <p className="font-mono text-xs text-white/40">Nothing sent yet.</p>
        ) : (
          <div className="space-y-1">
            {data.history.map((h) => (
              <div key={h.id} className="flex items-center justify-between border-b border-white/5 py-2.5">
                <span className="font-body text-sm text-white/80 truncate pr-3">{h.subject}</span>
                <span className="font-mono text-[10px] text-white/30 whitespace-nowrap">{(h.sent_at || "").slice(0, 10)} · {h.recipients} sent</span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

/* ---------- Subscribers ---------- */
function SubscribersTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/subscribers").then((r) => setData(r.data)).catch(() => setData({ count: 0, subscribers: [] })); }, []);
  if (!data) return <Loader2 className="animate-spin text-white/40" />;
  return (
    <SectionCard>
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">{data.count} subscribers</p>
      <div className="space-y-1 max-h-[500px] overflow-y-auto">
        {data.subscribers.map((s, i) => (
          <div key={i} className="flex items-center justify-between border-b border-white/5 py-2.5">
            <span className="font-body text-sm">{s.email}</span>
            <span className="font-mono text-[10px] text-white/30">{(s.created_at || "").slice(0, 10)}</span>
          </div>
        ))}
        {data.subscribers.length === 0 && <p className="font-mono text-xs text-white/40 py-4">No subscribers yet.</p>}
      </div>
    </SectionCard>
  );
}

/* ---------- Account ---------- */
function AccountTab({ email }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated.");
      setCur(""); setNw("");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Failed to change password."); } finally { setLoading(false); }
  };
  return (
    <SectionCard>
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">Account</p>
      <p className="font-body text-sm text-white/60">Signed in as <span className="text-white">{email}</span></p>
      <form onSubmit={submit} className="space-y-4 max-w-md pt-2">
        <Field label="Current password" type="password" value={cur} onChange={setCur} />
        <Field label="New password (min 6 chars)" type="password" value={nw} onChange={setNw} />
        <button data-testid="change-password-button" type="submit" disabled={loading} className="flex items-center gap-2 bg-accent text-white font-mono uppercase tracking-[0.15em] text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-60">
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Update Password"}
        </button>
      </form>
    </SectionCard>
  );
}
